
"use client";

import React, { useState } from "react";
import {
  Edit,
  Plus,
  Trash2,
  Eye,
  UploadIcon,
  DownloadCloudIcon,
  TrashIcon,
} from "lucide-react";
import BookingTable from "@/components/common/Table";
import Button from "@/components/Masters/Button";
import {
  useGetSightSeeingNameList,
  useUpdateSightSeeingStatus,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSidebarToggle } from "@/store/common";

interface Sightseeing {
  s_id: number;
  city_id: number;
  name: string;
  address: string;
  is_unique: boolean;
  coverage_km: string;
  sightseeing_cover: string;
  coverage_hrs: string;
  popularity: string;
  difficulty_level: string;
  meeting_point: string;
  created_by: number;
  status: number;
  city_name?: string;
  created_by_name?: string;
}

const SightseeingList: React.FC = () => {
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const router = useRouter();
  const user = useAuth();
  const { SidebarData } = useSidebarToggle();

  // Fetch sightseeing data
  const { data, isLoading, refetch } = useGetSightSeeingNameList();
  const updateStatus = useUpdateSightSeeingStatus();

  // Transform API data to match table structure
  const tableData =
    data?.data?.map((item: Sightseeing, index: number) => ({
      sno: index + 1,
      id: item.s_id,
      name: item.name,
      city: item.city_name,
      address: item.address,
      isunique: item.is_unique ? "Yes" : "No",
      covers: item.sightseeing_cover || "-",
      covergekm: item.coverage_km,
      coveragehrs: item.coverage_hrs,
      popularity: item.popularity,
      difficultylevel: item.difficulty_level,
      meetingpoint: item.meeting_point,
      createdby: item.created_by_name, // You might want to fetch user name
      status: item.status,
      originalData: item,
    })) || [];

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    {
      key: "sno",
      header: "S.No",
      sortable: true,
    },
    {
      key: "id",
      header: "ID",
      sortable: true,
    },
    {
      key: "name",
      header: "Name",
      sortable: true,
    },
    {
      key: "city",
      header: "City",
      sortable: true,
      render: (cityId: number) => (
        <span>{cityId}</span> // Replace with actual city name if you have it
      ),
    },
    {
      key: "address",
      header: "Address",
      sortable: true,
    },
    {
      key: "isunique",
      header: "Is Unique",
      sortable: true,
      render: (value: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            value === "Yes"
              ? "bg-green-100 text-green-800"
              : "bg-gray-100 text-gray-800"
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: "covers",
      header: "Cover Image",
      sortable: false,
      render: (cover: string) =>
        cover !== "-" ? (
          <div className="w-10 h-10 rounded overflow-hidden">
            <img
              src={cover}
              alt="Cover"
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          <span className="text-gray-400">No Image</span>
        ),
    },
    {
      key: "covergekm",
      header: "Coverage (KM)",
      sortable: true,
    },
    {
      key: "coveragehrs",
      header: "Coverage (Hrs)",
      sortable: true,
    },
    {
      key: "popularity",
      header: "Popularity",
      sortable: true,
      render: (popularity: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            popularity === "High"
              ? "bg-red-100 text-red-800"
              : popularity === "Medium"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-blue-100 text-blue-800"
          }`}
        >
          {popularity}
        </span>
      ),
    },
    {
      key: "difficultylevel",
      header: "Difficulty Level",
      sortable: true,
      render: (level: string) => (
        <span
          className={`px-2 py-1 rounded-full text-xs ${
            level === "Easy"
              ? "bg-green-100 text-green-800"
              : level === "Medium"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {level}
        </span>
      ),
    },
    {
      key: "meetingpoint",
      header: "Meeting Point",
      sortable: true,
    },
    {
      key: "createdby",
      header: "Created By",
      sortable: true,
      render: (userId: number) => (
        <span>{userId}</span> // Replace with actual user name if you have it
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (status: number) => (
        <div className="flex items-center">
          <div
            className={`w-3 h-3 rounded-full mr-2 ${
              status === 1 ? "bg-green-500" : "bg-red-500"
            }`}
          />
        </div>
      ),
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => (
        <div className="flex space-x-2">
          <button
            className="text-green-500 hover:text-green-700"
            onClick={() => router.push(`/sightseeing/${row.id}/view`)}
            title="View Details"
          >
            <Eye size={15} />
          </button>
          <button
            className="text-blue-500 hover:text-blue-700"
            onClick={() => router.push(`/sightseeing/${row.id}/edit`)}
            title="Edit"
          >
            <Edit size={15} />
          </button>
          <button
            className="text-red-500 hover:text-red-700"
            onClick={() => handleDeleteSingle(row.id)}
            title="Delete"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        ids: selectedRows,
        user_id: user?.user?.id.toString(),
        status: 1,
      },
      {
        onSuccess: () => {
          refetch();
          setSelectedRows([]);
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        ids: selectedRows,
        user_id: user?.user?.id.toString(),
        status: 0,
      },
      {
        onSuccess: () => {
          refetch();
          setSelectedRows([]);
        },
      }
    );
  };

  const handleDelete = () => {
    if (selectedRows.length === 0) return;

    if (confirm("Are you sure you want to delete selected sightseeing?")) {
      // Implement delete functionality
      console.log("Delete IDs:", selectedRows);
      setSelectedRows([]);
    }
  };

  const handleDeleteSingle = (id: number) => {
    if (confirm("Are you sure you want to delete this sightseeing?")) {
      // Implement single delete
      console.log("Delete ID:", id);
    }
  };

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row: any) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-screen overflow-auto">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">
          Sightseeing Management
        </h1>
        <div className="flex gap-2">
          <Link href="/sightseeing/general-information">
            <Button
              size="sm"
              icon={<Plus className="w-4 h-4" />}
              iconPosition="left"
            >
              Add Sightseeing
            </Button>
          </Link>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex justify-end gap-2 mb-4">
          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0}
          >
            Delete
          </Button>
        </div>
        <div
          className={`overflow-x-auto mx-auto
              md:max-w-[80vw] ${
                !SidebarData?.isCollapsed
                  ? "lg:max-w-[75vw]"
                  : "lg:max-w-[90vw]"
              }`}
        >
          <BookingTable
            data={tableData}
            columns={columns}
            heading={[]}
            searchable={true}
            filterable={true}
            sortable={true}
            selectable={true}
            onSelectionChange={setSelectedRows}
            loading={isLoading}
            renderHeader={() => (
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={
                        selectedRows.length === tableData.length &&
                        tableData.length > 0
                      }
                      onChange={handleSelectAll}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    S.No
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    City
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Address
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Is Unique
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cover
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coverage (KM)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Coverage (Hrs)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Popularity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Difficulty
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Meeting Point
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                </tr>
              </thead>
            )}
          />
        </div>
      </div>
    </div>
  );
};

export default SightseeingList;
